import { useContext, useEffect, useState, useCallback } from "react";
import { UserContext } from "../../../context/User.context";
import {
  Button,
  Space,
  Table,
  Input,
  notification,
  Pagination,
  Tag,
  Image,
  Tooltip,
  DatePicker,
} from "antd";
import { InfoCircleFilled, EyeOutlined, HomeFilled } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { Select } from "antd";
import { useHttp } from "../../../utils/DataHendler";
import { useMediaQuery } from "react-responsive";
import { formatDate } from "../../../utils/DateTime";

export const PatrolTable = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const { getData } = useHttp();
  const { user } = useContext(UserContext);
  const { Search } = Input;
  const { RangePicker } = DatePicker;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({});
  const [dataLocation, setLocation] = useState([]);
  const [locationSearchValue, setLocationSearchValue] = useState("");
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [searchingLocation, setSearchingLocation] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    // Reset filter dan pagination saat user berubah
    setFilters({});
    setPagination({ page: 1, limit: 10, total: 0, totalPages: 0 });
    fetchData({ page: 1, limit: 10 });
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    getLocation();
    // eslint-disable-next-line
  }, []);

  // Fetch patrol data sesuai struktur baru
  const fetchData = async (queryParams = {}) => {
    setLoading(true);
    try {
      // Pastikan page, limit, location_id selalu konsisten
      const merged = {
        page: queryParams.page || pagination.page || 1,
        limit: queryParams.limit || pagination.limit || 10,
        location_id:
          queryParams.location_id || filters.location_id || undefined,
        search: queryParams.search || filters.search || undefined,
        "date[]": queryParams["date[]"] || filters["date[]"] || undefined,
      };
      const response = await getData(
        `/security/patrol/inspection`,
        { params: merged },
        true
      );
      if (response.success) {
        const patrolData = response.data?.data || [];
        const paginationData = response.data?.pagination || {};
        setData(
          patrolData.map((item) => ({
            key: item.id,
            id: item.id,
            location_code: item.location_code,
            location_name: item.location_name,
            inspection_time: item.inspection_time,
            description: item.description,
            attachment: item.attachment,
            security_username: item.security_username,
            security_name: item.security_name,
          }))
        );
        setPagination({
          page: paginationData.page || merged.page,
          limit: paginationData.limit || merged.limit,
          total: paginationData.total || 0,
          totalPages: paginationData.totalPages || 1,
        });
      }
    } catch (error) {
      api.error({
        message: "Failed to fetch patrol data",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const getLocation = async (search = "") => {
    if (search) {
      setSearchingLocation(true);
    } else {
      setLoadingLocation(true);
    }
    
    try {
      const params = search ? { search, limit: 50 } : { limit: 50 };
      const response = await getData("/location/lookup", { params }, false);
      if (response.success) {
        const locations = response.data.data || [];
        setLocation(locations);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    } finally {
      setLoadingLocation(false);
      setSearchingLocation(false);
    }
  };

  // Handler untuk perubahan halaman
  const handlePageChange = (page, pageSize) => {
    // page dan limit harus ikut filter location_id/search
    const newFilters = { ...filters, page, limit: pageSize };
    setPagination((prev) => ({ ...prev, page, limit: pageSize }));
    setFilters(newFilters);
    fetchData(newFilters);
  };

  // Debounced handler untuk filter location
  const debouncedFilterChange = useCallback(
    debounce((newFilters) => {
      fetchData(newFilters);
    }, 900),
    [pagination.limit]
  );

  const handleFilterChange = (changed) => {
    // Reset ke page 1 jika filter berubah
    const newFilters = {
      ...filters,
      ...changed,
      page: 1,
      limit: pagination.limit,
    };
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
    debouncedFilterChange(newFilters);
  };

  // Debounced search for locations
  const debouncedSearchLocation = useCallback(
    debounce((searchValue) => {
      getLocation(searchValue);
    }, 500),
    []
  );

  // Handle location search input change
  const handleLocationSearch = (value) => {
    setLocationSearchValue(value);
    if (value) {
      debouncedSearchLocation(value);
    } else {
      // If search is cleared, load all locations
      getLocation("");
    }
  };

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value) => {
      const newFilters = {
        ...filters,
        search: value || undefined,
        page: 1,
        limit: pagination.limit,
      };
      setFilters(newFilters);
      setPagination((prev) => ({ ...prev, page: 1 }));
      fetchData(newFilters);
    }, 600),
    [filters, pagination.limit]
  );

  // Table columns sesuai data
  const columns = [
    {
      title: "Location Code",
      dataIndex: "location_code",
      key: "location_code",
      width: 120,
      ellipsis: true,
    },
    {
      title: "Location Name",
      dataIndex: "location_name",
      key: "location_name",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Inspection Time",
      dataIndex: "inspection_time",
      key: "inspection_time",
      width: 170,
      render: (val) =>
        val
          ? formatDate(val)
          : "-",
      ellipsis: true,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 220,
      ellipsis: true,
      render: (val) =>
        val ? (
          <Tooltip title={val}>
            <span>{val}</span>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: "Attachment",
      dataIndex: "attachment",
      key: "attachment",
      width: 120,
      render: (attachments) =>
        attachments && attachments.length > 0 ? (
          <Image
            src={attachments[0]}
            width={60}
            height={40}
            style={{ objectFit: "cover", borderRadius: 4 }}
            alt="Attachment"
            preview={{ src: attachments[0] }}
          />
        ) : (
          <Tag color="red">No Image</Tag>
        ),
    },
    {
      title: "Security",
      dataIndex: "security_username",
      key: "security_username",
      width: 120,
      ellipsis: true,
      render: (val, record) =>
        record.security_name ? `${record.security_name} (${val})` : val || "-",
    },
    {
      title: "Actions",
      key: "action",
      width: 90,
      fixed: "right",
      render: (_, record) => (
        <Button
          icon={<EyeOutlined />}
          onClick={() => navigate(`/user/patrol/detail/${record.id}`)}
        />
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      {/* <div
        style={{
          marginBottom: "20px",
          padding: "16px",
          backgroundColor: "#fafafa",
          borderRadius: "8px",
          border: "1px solid #e8e8e8",
        }}
      > */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "flex-start",
            alignItems: isMobile ? "stretch" : "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          {/* Location Filter */}
          <div style={{ width: isMobile ? "100%" : "220px" }}>
            <Select
              showSearch
              placeholder="Select Location"
              allowClear
              loading={loadingLocation || searchingLocation}
              onSearch={handleLocationSearch}
              searchValue={locationSearchValue}
              filterOption={false}
              notFoundContent={loadingLocation || searchingLocation ? "Searching..." : "No data"}
              options={Array.isArray(dataLocation) ? dataLocation.map((el) => ({
                value: el.id,
                label: `${el.name} (${el.code})` || el.location_name || `Location ${el.id}`,
              })) : []}
              onChange={(val) => {
                handleFilterChange({ location_id: val || null });
              }}
              onClear={() => {
                setLocationSearchValue("");
                getLocation("");
              }}
              style={{ width: "100%" }}
              size="middle"
            />
          </div>

          {/* Date Range Filter */}
          <div style={{ width: isMobile ? "100%" : "260px" }}>
            <RangePicker
              allowClear
              placeholder={["Start Date", "End Date"]}
              style={{ width: "100%" }}
              size="middle"
              onCalendarChange={(dates, dateString) => {
                if (Array.isArray(dateString) && dateString.length === 2) {
                  const [startDate, endDate] = dateString.map((d) => d.trim());
                  const isValidDate = (dateString) =>
                    /^\d{4}-\d{2}-\d{2}$/.test(dateString);

                  if (isValidDate(startDate) && isValidDate(endDate)) {
                    handleFilterChange({ "date[]": [startDate, endDate] });
                  } else {
                    handleFilterChange({ "date[]": null });
                  }
                } else {
                  handleFilterChange({ "date[]": null });
                }
              }}
            />
          </div>

          {/* Search Filter */}
          <div style={{ width: isMobile ? "100%" : "240px" }}>
            <Search
              placeholder="Search location or description"
              allowClear
              onChange={(e) => debouncedSearch(e.target.value.trim())}
              enterButton
              size="middle"
              style={{ width: "100%" }}
            />
          </div>
        </div>
      {/* </div> */}
      <Table
        dataSource={data}
        columns={columns}
        loading={loading}
        scroll={{ x: 1100, y: 550 }}
        pagination={false}
        size="small"
        style={{ width: "100%" }}
        bordered
      />
      <Pagination
        current={pagination.page}
        pageSize={pagination.limit}
        total={pagination.total}
        onChange={handlePageChange}
        showSizeChanger={false}
        style={{ marginTop: 20, textAlign: "center" }}
      />
    </>
  );
};
